import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Avatar,
  AvatarBadge,
  Flex,
  Divider,
  useColorModeValue,
  Circle,
  useTheme,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { Search, MoreVertical, Edit, Filter, Package, Image, Film, Music, FileText } from 'lucide-react';
import { useSelector } from 'react-redux';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';

const formatChatTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Today – show time
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' }); // e.g. "Mon"
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }); // e.g. "Mar 18"
  }
};

const Sidebar = ({ conversations, activeChatId, onSelectChat, onFindItem }) => {
  const itemData = useSelector((state) => state.item?.list);
  const theme = useTheme();
  const [selectedItem, setSelectedItem] = useState(null);
  const { isOpen: isFindItemOpen, onOpen: onOpenFindItem, onClose: onCloseFindItem } = useDisclosure();
  const [findItemId, setFindItemId] = useState(null);

  // Professional color palette for a premium feel
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const sidebarHeaderBg = useColorModeValue('white', 'gray.800');
  const hoverColor = useColorModeValue('gray.50', 'gray.700');
  const activeChatBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const activeBorderColor = useColorModeValue('blue.500', 'blue.400');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const searchInputBg = useColorModeValue('gray.50', 'gray.700');

  const handleitemFind = (itemId) => {
    const item = itemData.find((item) => item.id === itemId);
    onFindItem(item.name);
    onCloseFindItem();
  };

  return (
    <Box
      w={{ base: 'full', md: '360px' }}
      h="full"
      bg={bgColor}
      borderRight="1px"
      borderColor={borderColor}
      display="flex"
      flexDirection="column"
      position="relative"
      zIndex="50"
    >
      {/* Header with Title and Actions */}
      <Box p={6} pb={4} bg={sidebarHeaderBg}>
        <HStack justify="space-between" mb={5}>
          <Text fontWeight="800" fontSize="2xl" letterSpacing="tight" color={textColor}>
            Messages
          </Text>
          <HStack spacing={1}>
            {/* <Circle
              size="36px"
              bg="transparent"
              _hover={{ bg: hoverColor }}
              cursor="pointer"
              transition="all 0.2s"
            >
              <Edit size={18} color={theme.colors.gray[500]} />
            </Circle> */}
            <Menu>
              <MenuButton as={Box} cursor="pointer">
                <Circle
                  size="36px"
                  bg="transparent"
                  _hover={{ bg: hoverColor }}
                  cursor="pointer"
                  transition="all 0.2s"
                >
                  <MoreVertical size={18} color={theme.colors.gray[500]} />
                </Circle>
              </MenuButton>
              <MenuList zIndex={100} boxShadow="md" borderColor={borderColor} bg={bgColor}>
                <MenuItem _hover={{ bg: hoverColor }} onClick={onOpenFindItem} color={textColor} bg="transparent">
                  Find Item
                </MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>

        {/* Item Selection and Action Button - NEWLY ADDED */}
        {/* <HStack spacing={3} mb={4}>
           <Box flex="1">
            {renderDynamicDropdown(
              'itemId',
              'Item',
              itemData,
              {
                value: selectedItem,
                onChange: (value) => setSelectedItem(value),
                focusBorderColor: 'blue.500',
                _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
                showCustomNoOptions: true,
                openfrom: 'item',
                isClearable: false,
                onKeyDown: (e) => {

                },
              },
              {},
              textColor,
              false,
              'Select Item'
            )}
          </Box>
          <Button
            colorScheme="blue"
            borderRadius="xl"
            h="44px"
            px={8}
            fontSize="sm"
            fontWeight="700"
            shadow="md"
            _hover={{ shadow: 'lg', transform: 'translateY(-1px)' }}
          >
            New
          </Button>
        </HStack> */}

        {/* Search and Filters */}
        <HStack spacing={3}>
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="44px">
              <Search size={16} color={theme.colors.gray[400]} />
            </InputLeftElement>
            <Input
              placeholder="Search conversations..."
              variant="filled"
              bg={searchInputBg}
              borderRadius="xl"
              h="44px"
              fontSize="sm"
              _focus={{ bg: useColorModeValue('white', 'gray.600'), borderColor: 'blue.400', boxShadow: 'none' }}
              _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
            />
          </InputGroup>
          {/* <Circle
            size="44px"
            bg={sidebarHeaderBg}
            border="1px solid"
            borderColor={borderColor}
            _hover={{ bg: hoverColor }}
            cursor="pointer"
          >
            <Filter size={16} color={theme.colors.gray[500]} />
          </Circle> */}
        </HStack>
      </Box>

      <Divider opacity={0.6} />

      {/* Chat List with Virtual Scrolling effect via Box overflow */}
      <Box
        flex="1"
        overflowY="auto"
        pt={3}
        px={3}
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: theme.colors.gray[200], borderRadius: '10px' },
        }}
      >
        <VStack spacing={1} align="stretch">
          {[...conversations]
            .sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime))
            .map((chat) => (
              <HStack
                key={chat.id}
                p={4}
                spacing={4}
                borderRadius="xl"
                cursor="pointer"
                bg={activeChatId === chat.id ? activeChatBg : 'transparent'}
                _hover={{ bg: activeChatId === chat.id ? activeChatBg : hoverColor }}
                onClick={() => {
                  if (activeChatId === chat.id) {
                    console.log('Already in this chat');
                    return;
                  }
                  onSelectChat(chat, activeChatId);
                }}
                transition="all 0.2s"
                position="relative"
                role="group"
              >
                {/* Active indicator line */}
                {activeChatId === chat.id && (
                  <Box
                    position="absolute"
                    left="0"
                    h="60%"
                    w="4px"
                    bg={activeBorderColor}
                    borderRadius="full"
                  />
                )}

                <Box position="relative">
                  <Avatar
                    size="md"
                    name={chat.name}
                    src={chat.avatar}
                    boxShadow="sm"
                    border="2px solid"
                    borderColor={activeChatId === chat.id ? 'blue.500' : 'transparent'}
                  >
                    {chat.isOnline && (
                      <AvatarBadge
                        boxSize="1em"
                        bg="green.500"
                        borderColor={bgColor}
                        borderWidth="2px"
                      />
                    )}
                  </Avatar>
                </Box>

                <Flex flex="1" direction="column" minW="0">
                  <HStack justify="space-between" mb={1}>
                    <Text
                      fontWeight={chat.unreadCount > 0 ? "800" : "700"}
                      fontSize="sm"
                      color={textColor}
                      isTruncated
                    >
                      {chat.name}
                    </Text>
                    <Text fontSize="xs" color={chat.unreadCount > 0 ? "blue.500" : mutedTextColor} fontWeight="500">
                      {formatChatTime(chat.lastMessageTime)}
                    </Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text
                      fontSize="xs"
                      color={chat.unreadCount > 0 ? textColor : mutedTextColor}
                      fontWeight={chat.unreadCount > 0 ? "600" : "500"}
                      isTruncated
                    >
                      {chat.isTyping ? (
                        <HStack spacing={1} align="center">
                          <style>{`
                            @keyframes typingBounce {
                              0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
                              40% { transform: translateY(-4px); opacity: 1; }
                            }
                            .typing-dot {
                              width: 5px;
                              height: 5px;
                              border-radius: 50%;
                              background-color: #38A169;
                              display: inline-block;
                              animation: typingBounce 1.2s infinite ease-in-out;
                            }
                            .typing-dot:nth-child(2) { animation-delay: 0.2s; }
                            .typing-dot:nth-child(3) { animation-delay: 0.4s; }
                          `}</style>
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                          <span className="typing-dot" />
                          <span style={{ color: '#38A169', fontWeight: 600, fontSize: '11px', marginLeft: '2px' }}>Typing...</span>
                        </HStack>
                      ) : chat.lastMessageType === "ITEM" ? (
                        <HStack spacing={1} align="center">
                          <Package size={11} />
                          <span>ITEM</span>
                        </HStack>
                      ) : chat.lastMessageType === "IMG" ? (
                        <HStack spacing={1} align="center">
                          <Image size={11} />
                          <span>Photo</span>
                        </HStack>
                      ) : chat.lastMessageType === "VIDEO" ? (
                        <HStack spacing={1} align="center">
                          <Film size={11} />
                          <span>Video</span>
                        </HStack>
                      ) : chat.lastMessageType === "AUDIO" ? (
                        <HStack spacing={1} align="center">
                          <Music size={11} />
                          <span>Audio</span>
                        </HStack>
                      ) : chat.lastMessageType === "DOC" ? (
                        <HStack spacing={1} align="center">
                          <FileText size={11} />
                          <span>Document</span>
                        </HStack>
                      ) : (
                        chat.lastMessage
                      )}
                    </Text>
                    {chat.unreadCount > 0 && (
                      <Circle
                        size="22px"
                        bg="blue.500"
                        color="white"
                        fontSize="10px"
                        fontWeight="800"
                        shadow="md"
                      >
                        {chat.unreadCount}
                      </Circle>
                    )}
                  </HStack>
                </Flex>
              </HStack>
            ))}
        </VStack>
      </Box>

      {/* Find Item Modal */}
      <Modal isOpen={isFindItemOpen} onClose={() => { onCloseFindItem(); setFindItemId(null); }} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(5px)" />
        <ModalContent bg={bgColor} borderColor={borderColor} borderWidth="1px" borderRadius="xl" shadow="2xl">
          <ModalHeader color={textColor} borderBottom="1px" borderColor={borderColor} py={4}>
            Find Item
          </ModalHeader>
          <ModalCloseButton color={mutedTextColor} mt={2} />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch" w="full">
              <Box>
                {renderDynamicDropdown(
                  'findItemId',
                  'Item',
                  itemData,
                  {
                    value: findItemId,
                    onChange: (value) => setFindItemId(value),
                    focusBorderColor: 'blue.500',
                    _focus: { boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)' },
                    isClearable: true,
                    menuPortalTarget: document.body,
                    styles: {
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    },
                    onKeyDown: (e) => {
                    },
                  },
                  {},
                  textColor,
                  false,
                  'Search to find an item...'
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter borderTop="1px" borderColor={borderColor} py={4} gap={3}>
            <Button variant="ghost" onClick={() => { onCloseFindItem(); setFindItemId(null); }} borderRadius="xl" _hover={{ bg: hoverColor }}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              borderRadius="xl"
              px={6}
              onClick={() => {
                // TODO: Apply whatever logic is needed on Find
                handleitemFind(findItemId);
                onCloseFindItem();
                setFindItemId(null);
              }}
              isDisabled={!findItemId}
            >
              Find
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Sidebar;
